// components/orders/OrderDetailsModal.tsx
import React, { useState } from 'react'
import {
  X,
  Printer,
  Send,
  Mail,
  MessageSquare,
  CreditCard,
  Users,
  MapPin,
  Clock,
  Calendar,
  DollarSign,
  Package,
  Truck,
  Home,
  Copy,
  Download,
  RotateCcw,
  Ban,
  FileText,
  History,
  MoreVertical,
  Share2,
  RotateCcw as RotateCw,
  Archive,
  ExternalLink
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import toast from 'react-hot-toast'

// Import your types or define them here
interface Order {
  id: string
  order_number: string
  order_date: string
  customer_id?: string
  user_id?: string
  cash_register_id?: string
  business_day_id?: string
  table_id?: string
  room_id?: string
  status: 'draft' | 'open' | 'parked' | 'completed' | 'voided' | 'refunded'
  order_type: 'sale' | 'return' | 'exchange' | 'quote' | 'refund'
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  paid_amount: number
  change_amount: number
  is_parked: boolean
  is_locked: boolean
  is_voided: boolean
  void_reason_id?: string
  voided_by?: string
  voided_date?: string
  payment_status: 'unpaid' | 'partial' | 'paid' | 'refunded'
  payment_status_date?: string
  notes?: string
  created_at: string
  updated_at: string
  fiscal_receipt_number?: string
  fiscal_receipt_date?: string
  delivery_address?: string
  delivery_date?: string
  delivery_time?: string
  delivery_status?: string
  delivery_driver_id?: string
  delivery_fee: number
  total_tips: number
  service_charge: number
  customer_count: number
  table_number?: string
  waiter_id?: string
  kitchen_status?: string
  preparation_time?: number
  source_channel?: string
  sales_channel?: string
  guest_count: number
  special_instructions?: string
  is_gift: boolean
  gift_wrap_fee: number
  rounding_adjustment: number
  points_earned: number
  points_redeemed: number
  points_balance_after: number
  affiliate_id?: string
  ip_address?: string
  user_agent?: string

  // Relations
  customer?: Customer
  user?: User
  table?: Table
  room?: Room
  waiter?: User
  delivery_driver?: Driver
  business_day?: BusinessDay
  cash_register?: CashRegister
  order_items?: OrderItem[]
  order_payments?: OrderPayment[]
  order_rounds?: OrderRound[]
  commissions?: Commission[]
  promotions?: Promotion[]
  fiscal_document?: FiscalDocument
}

interface Customer {
  id: string
  code: string
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  phone?: string
  mobile?: string
  customer_type: 'individual' | 'company'
  loyalty_points_balance: number
  vip_status: boolean
  blacklisted: boolean
  total_lifetime_value: number
  average_order_value: number
  visit_count: number
  last_visit_date?: string
}

interface User {
  id: string
  username: string
  first_name?: string
  last_name?: string
  role: string
  is_active: boolean
  authorization_level: number
}

interface Table {
  id: string
  name: string
  capacity: number
  room_id: string
  is_active: boolean
}

interface Room {
  id: string
  name: string
  store_id: string
  sort_order: number
}

interface Driver {
  id: string
  driver_code: string
  first_name: string
  last_name: string
  phone?: string
  status: string
}

interface BusinessDay {
  id: string
  opened_date: string
  closed_date?: string
  status: string
  total_sales: number
  total_transactions: number
}

interface CashRegister {
  id: string
  name: string
  code: string
  terminal_id?: string
  is_active: boolean
}

interface OrderItem {
  id: string
  order_id: string
  product_id?: string
  product_code?: string
  product_name: string
  line_number: number
  parent_line_number?: number
  quantity: number
  unit_price: number
  original_price: number
  discount_percent: number
  discount_amount: number
  discount_type?: string
  tax_percent: number
  tax_amount: number
  total_amount: number
  serial_number?: string
  lot_number?: string
  return_quantity: number
  weight_sold?: number
  preparation_instructions?: string
  course_number?: number
  seat_number?: number
  kitchen_status?: string
  round_number: number
  is_confirmed: boolean
  is_canceled: boolean
  is_voided: boolean
  voided_by?: string
  voided_date?: string
  notes?: string
  created_at: string
  updated_at: string
  cost_price?: number
  preparation_status?: string
  prepared_by?: string
  prepared_at?: string
  served_by?: string
  served_at?: string
  allergen_warnings?: string
  modifier_total: number

  // Relations
  product?: Product
  order_item_modifiers?: OrderItemModifier[]
  order_item_taxes?: OrderItemTax[]
  order_item_rounds?: OrderItemRound[]
}

interface Product {
  id: string
  code: string
  name: string
  description?: string
  price: number
  cost_price?: number
  category_id?: string
  is_service: boolean
  track_inventory: boolean
  is_active: boolean
  allow_decimal_quantities: boolean
  stock_control_type?: string
  is_modifier: boolean
  modifiers_allowed: boolean
  print_kitchen_receipt: boolean
  preparation_time_minutes?: number
  kitchen_printer_id?: string
}

interface OrderItemModifier {
  id: string
  order_item_id: string
  modifier_option_id: string
  quantity: number
  price_adjustment: number
  created_at: string

  modifier_option?: ModifierOption
}

interface ModifierOption {
  id: string
  name: string
  price_adjustment: number
  is_active: boolean
  sort_order: number
  modifier_group_id: string
}

interface OrderItemTax {
  id: string
  order_item_id: string
  tax_rate_id: string
  tax_amount: number
  created_at: string

  tax_rate?: TaxRate
}

interface TaxRate {
  id: string
  name: string
  code: string
  rate: number
  is_active: boolean
}

interface OrderItemRound {
  order_item_id: string
  order_round_id: string
  quantity: number
}

interface OrderPayment {
  id: string
  order_id: string
  payment_type_id: string
  amount: number
  tendered_amount?: number
  change_amount: number
  payment_date: string
  reference_number?: string
  card_type?: string
  card_last_four?: string
  authorization_code?: string
  terminal_id?: string
  is_voided: boolean
  void_reason_id?: string
  voided_by?: string
  voided_at?: string
  notes?: string

  // Relations
  payment_type?: PaymentType
  void_reason?: VoidReason
}

interface PaymentType {
  id: string
  name: string
  code: string
  is_active: boolean
  opens_cash_drawer: boolean
  requires_reference: boolean
  allows_change: boolean
  is_default: boolean
}

interface VoidReason {
  id: string
  name: string
  description?: string
  is_active: boolean
}

interface OrderRound {
  id: string
  order_id: string
  round_number: number
  created_at: string

  order_item_rounds?: OrderItemRound[]
}

interface Commission {
  id: string
  employee_id: string
  order_id: string
  commission_date: string
  commission_type?: string
  base_amount: number
  commission_rate: number
  commission_amount: number
  status: string
  paid_date?: string
}

interface Promotion {
  id: string
  code?: string
  name: string
  promotion_type: string
  discount_percent?: number
  discount_amount?: number
  start_date: string
  end_date: string
  is_active: boolean
}

interface FiscalDocument {
  id: string
  document_number: string
  document_type: string
  order_id: string
  cash_register_id: string
  issue_date: string
  total_amount: number
  tax_amount: number
  signature?: string
  is_voided: boolean
}

import { StatusBadge } from '../ui/StatusBadge'
import { CurrencyDisplay } from '../ui/CurrencyDisplay'
import { LoadingSpinner } from '../ui/LoadingSpinner'

interface OrderDetailsModalProps {
  order: Order
  isOpen: boolean
  onClose: () => void
  onVoid?: (orderId: string, reason: string) => Promise<void>
  onRefund?: (orderId: string, data: any) => Promise<void>
  onPrintReceipt?: (orderId: string, type: 'customer' | 'kitchen' | 'fiscal') => Promise<void>
  onSendReceipt?: (orderId: string, method: 'email' | 'sms') => Promise<void>
  onReopen?: (orderId: string) => Promise<void>
  onTransferTable?: (orderId: string, tableId: string) => Promise<void>
  onSplitOrder?: (orderId: string, data: any) => Promise<void>
  onMergeOrders?: (orderId: string, orderIds: string[]) => Promise<void>
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  isOpen,
  onClose,
  onVoid,
  onRefund,
  onPrintReceipt,
  onSendReceipt,
  onReopen,
  onTransferTable: _onTransferTable,
  onSplitOrder: _onSplitOrder,
  onMergeOrders: _onMergeOrders
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'items' | 'payments' | 'history'>('details')
  const [isLoading, setIsLoading] = useState(false)
  const [showVoidDialog, setShowVoidDialog] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [showRefundDialog, setShowRefundDialog] = useState(false)
  const [refundAmount, setRefundAmount] = useState(order.paid_amount)
  const [refundMethod, setRefundMethod] = useState('original')

  if (!isOpen) return null

  const handleVoid = async () => {
    if (!onVoid) return
    setIsLoading(true)
    try {
      await onVoid(order.id, voidReason)
      toast.success('Order voided successfully')
      setShowVoidDialog(false)
      setVoidReason('')
      onClose()
    } catch (error) {
      toast.error('Failed to void order')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefund = async () => {
    if (!onRefund) return
    setIsLoading(true)
    try {
      await onRefund(order.id, {
        amount: refundAmount,
        method: refundMethod,
        reason: 'Customer request'
      })
      toast.success('Refund processed successfully')
      setShowRefundDialog(false)
      onClose()
    } catch (error) {
      toast.error('Failed to process refund')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePrint = async (type: 'customer' | 'kitchen' | 'fiscal') => {
    if (!onPrintReceipt) return
    setIsLoading(true)
    try {
      await onPrintReceipt(order.id, type)
    } catch (error) {
      toast.error('Failed to print receipt')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSend = async (method: 'email' | 'sms') => {
    if (!onSendReceipt) return
    setIsLoading(true)
    try {
      await onSendReceipt(order.id, method)
    } catch (error) {
      toast.error(`Failed to send ${method} receipt`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReopen = async () => {
    if (!onReopen) return
    setIsLoading(true)
    try {
      await onReopen(order.id)
      toast.success('Order reopened successfully')
      onClose()
    } catch (error) {
      toast.error('Failed to reopen order')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyOrderNumber = () => {
    navigator.clipboard.writeText(order.order_number)
    toast.success('Order number copied to clipboard')
  }

  const calculateItemTotals = () => {
    const subtotal = order.order_items?.reduce((sum, item) => sum + item.total_amount, 0) || 0
    const tax = order.order_items?.reduce((sum, item) => sum + item.tax_amount, 0) || 0
    const discount = order.order_items?.reduce((sum, item) => sum + item.discount_amount, 0) || 0
    const modifierTotal = order.order_items?.reduce((sum, item) => sum + (item.modifier_total || 0), 0) || 0

    return { subtotal, tax, discount, modifierTotal }
  }

  const itemTotals = calculateItemTotals()

  const renderOrderDetails = () => (
    <div className="space-y-6">
      {/* Order Header */}
      <div className="bg-gray-750 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-bold text-white">Order #{order.order_number}</h3>
              <button
                onClick={handleCopyOrderNumber}
                className="p-1 hover:bg-gray-700 rounded"
                title="Copy order number"
              >
                <Copy size={16} className="text-gray-400" />
              </button>
              {order.fiscal_receipt_number && (
                <span className="text-sm text-gray-400">
                  Fiscal: {order.fiscal_receipt_number}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                {format(parseISO(order.order_date), 'MMM dd, yyyy')}
              </div>
              <div className="flex items-center gap-1">
                <Clock size={14} />
                {format(parseISO(order.order_date), 'HH:mm:ss')}
              </div>
              <div className="flex items-center gap-1">
                <Users size={14} />
                {order.guest_count || order.customer_count || 1} guest(s)
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge
              status={order.status}
              variants={{
                draft: { color: 'gray', label: 'Draft' },
                open: { color: 'blue', label: 'Open' },
                parked: { color: 'yellow', label: 'Parked' },
                completed: { color: 'green', label: 'Completed' },
                voided: { color: 'red', label: 'Voided' },
                refunded: { color: 'orange', label: 'Refunded' }
              }}
            />
            <StatusBadge
              status={order.payment_status}
              variants={{
                unpaid: { color: 'red', label: 'Unpaid' },
                partial: { color: 'yellow', label: 'Partial' },
                paid: { color: 'green', label: 'Paid' },
                refunded: { color: 'orange', label: 'Refunded' }
              }}
            />
          </div>
        </div>
      </div>

      {/* Customer & Location */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Customer Info */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Users size={18} />
            Customer Information
          </h4>
          {order.customer ? (
            <div className="space-y-2">
              <div>
                <div className="text-sm text-gray-400">Name</div>
                <div className="text-white font-medium">
                  {order.customer.first_name} {order.customer.last_name}
                  {order.customer.company_name && ` (${order.customer.company_name})`}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Contact</div>
                <div className="text-white">
                  {order.customer.email && <div>{order.customer.email}</div>}
                  {order.customer.phone && <div>{order.customer.phone}</div>}
                </div>
              </div>
              {order.customer.loyalty_points_balance > 0 && (
                <div>
                  <div className="text-sm text-gray-400">Loyalty Points</div>
                  <div className="text-yellow-400 font-medium">
                    {order.customer.loyalty_points_balance.toLocaleString()} points
                  </div>
                </div>
              )}
              {order.customer.vip_status && (
                <StatusBadge status="VIP" color="yellow" />
              )}
            </div>
          ) : (
            <div className="text-gray-500 italic">Walk-in customer</div>
          )}
        </div>

        {/* Location & Service */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
            <MapPin size={18} />
            Location & Service
          </h4>
          <div className="space-y-3">
            {order.table && (
              <div>
                <div className="text-sm text-gray-400">Table</div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-purple-900 text-purple-200 rounded text-sm">
                    {order.table.name}
                  </span>
                  <span className="text-gray-400 text-sm">
                    (Capacity: {order.table.capacity})
                  </span>
                  {order.room && (
                    <span className="text-gray-400 text-sm">
                      in {order.room.name}
                    </span>
                  )}
                </div>
              </div>
            )}

            {order.delivery_address && (
              <div>
                <div className="text-sm text-gray-400">Delivery Address</div>
                <div className="text-white">{order.delivery_address}</div>
                {order.delivery_status && (
                  <div className="mt-1">
                    <StatusBadge
                      status={order.delivery_status}
                      variants={{
                        pending: { color: 'yellow', label: 'Pending' },
                        assigned: { color: 'blue', label: 'Assigned' },
                        in_transit: { color: 'purple', label: 'En Route' },
                        delivered: { color: 'green', label: 'Delivered' },
                        failed: { color: 'red', label: 'Failed' }
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="text-sm text-gray-400">Service Type</div>
              <div className="flex items-center gap-2">
                {order.delivery_address ? (
                  <>
                    <Truck size={16} className="text-blue-400" />
                    <span className="text-white">Delivery</span>
                  </>
                ) : order.table ? (
                  <>
                    <Home size={16} className="text-green-400" />
                    <span className="text-white">Dine-in</span>
                  </>
                ) : (
                  <>
                    <Package size={16} className="text-orange-400" />
                    <span className="text-white">Takeout</span>
                  </>
                )}
              </div>
            </div>

            {order.waiter && (
              <div>
                <div className="text-sm text-gray-400">Waiter</div>
                <div className="text-white">{order.waiter.first_name} {order.waiter.last_name}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="text-white font-semibold mb-4">Order Summary</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Subtotal</span>
            <CurrencyDisplay value={itemTotals.subtotal} />
          </div>

          {itemTotals.modifierTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Modifiers</span>
              <CurrencyDisplay value={itemTotals.modifierTotal} />
            </div>
          )}

          {order.discount_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Discount</span>
              <span className="text-orange-400">
                -<CurrencyDisplay value={order.discount_amount} />
              </span>
            </div>
          )}

          {order.service_charge > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Service Charge</span>
              <CurrencyDisplay value={order.service_charge} />
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-gray-400">Tax</span>
            <CurrencyDisplay value={itemTotals.tax} />
          </div>

          {order.delivery_fee > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Delivery Fee</span>
              <CurrencyDisplay value={order.delivery_fee} />
            </div>
          )}

          {order.gift_wrap_fee > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Gift Wrap</span>
              <CurrencyDisplay value={order.gift_wrap_fee} />
            </div>
          )}

          {order.rounding_adjustment !== 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Rounding Adjustment</span>
              <CurrencyDisplay value={order.rounding_adjustment} />
            </div>
          )}

          <div className="border-t border-gray-700 pt-2 mt-2">
            <div className="flex justify-between text-lg font-bold">
              <span className="text-white">Total</span>
              <CurrencyDisplay value={order.total_amount} size="lg" weight="bold" />
            </div>
          </div>

          {order.paid_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Paid Amount</span>
              <CurrencyDisplay value={order.paid_amount} color="text-green-400" />
            </div>
          )}

          {order.change_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Change Due</span>
              <CurrencyDisplay value={order.change_amount} color="text-yellow-400" />
            </div>
          )}

          {order.total_tips > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">Tips</span>
              <CurrencyDisplay value={order.total_tips} color="text-green-400" />
            </div>
          )}

          {/* Loyalty Points */}
          {(order.points_earned > 0 || order.points_redeemed > 0) && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="text-sm text-gray-400 mb-1">Loyalty Points</div>
              <div className="flex justify-between text-sm">
                <div>
                  {order.points_earned > 0 && (
                    <span className="text-green-400">+{order.points_earned} earned</span>
                  )}
                  {order.points_redeemed > 0 && (
                    <span className="text-red-400 ml-2">-{order.points_redeemed} redeemed</span>
                  )}
                </div>
                <div className="text-yellow-400">
                  Balance: {order.points_balance_after} points
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notes & Special Instructions */}
      {(order.notes || order.special_instructions) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3">Notes & Instructions</h4>
          {order.notes && (
            <div className="mb-3">
              <div className="text-sm text-gray-400 mb-1">Order Notes</div>
              <div className="text-white bg-gray-750 rounded p-3">{order.notes}</div>
            </div>
          )}
          {order.special_instructions && (
            <div>
              <div className="text-sm text-gray-400 mb-1">Special Instructions</div>
              <div className="text-white bg-gray-750 rounded p-3">{order.special_instructions}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )

  const renderOrderItems = () => (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Item</th>
              <th className="text-right py-3 px-4 text-gray-400 font-medium">Qty</th>
              <th className="text-right py-3 px-4 text-gray-400 font-medium">Price</th>
              <th className="text-right py-3 px-4 text-gray-400 font-medium">Total</th>
              <th className="text-center py-3 px-4 text-gray-400 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {order.order_items?.map((item) => (
              <tr key={item.id} className="border-b border-gray-800 hover:bg-gray-750">
                <td className="py-3 px-4">
                  <div>
                    <div className="text-white font-medium">{item.product_name}</div>
                    {item.product_code && (
                      <div className="text-xs text-gray-500">{item.product_code}</div>
                    )}
                    {item.notes && (
                      <div className="text-xs text-blue-400 italic mt-1">{item.notes}</div>
                    )}
                    {item.order_item_modifiers && item.order_item_modifiers.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-500 mb-1">Modifiers:</div>
                        {item.order_item_modifiers.map((mod) => (
                          <div key={mod.id} className="text-xs text-gray-400 ml-2">
                            • {mod.modifier_option?.name} 
                            {mod.price_adjustment !== 0 && (
                              <span className="text-gray-500 ml-1">
                                (+<CurrencyDisplay value={mod.price_adjustment} size="xs" showSymbol={false} />)
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="text-white font-medium">{item.quantity.toFixed(3)}</div>
                  {item.return_quantity > 0 && (
                    <div className="text-xs text-red-400">-{item.return_quantity.toFixed(3)} returned</div>
                  )}
                </td>
                <td className="py-3 px-4 text-right">
                  <CurrencyDisplay value={item.unit_price} />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="text-white font-medium">
                    <CurrencyDisplay value={item.total_amount + (item.modifier_total || 0)} />
                  </div>
                  {item.discount_amount > 0 && (
                    <div className="text-xs text-orange-400">
                      -<CurrencyDisplay value={item.discount_amount} size="xs" showSymbol={false} />
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 text-center">
                  <StatusBadge
                    status={item.preparation_status || 
                      (item.is_voided ? 'voided' : 
                       item.is_canceled ? 'canceled' : 
                       item.is_confirmed ? 'confirmed' : 'pending')}
                    variants={{
                      pending: { color: 'yellow', label: 'Pending' },
                      preparing: { color: 'blue', label: 'Preparing' },
                      ready: { color: 'green', label: 'Ready' },
                      served: { color: 'purple', label: 'Served' },
                      confirmed: { color: 'green', label: 'Confirmed' },
                      canceled: { color: 'red', label: 'Canceled' },
                      voided: { color: 'gray', label: 'Voided' }
                    }}
                    size="sm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Item Totals */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-sm text-gray-400">Total Items</div>
            <div className="text-2xl font-bold text-white">
              {order.order_items?.length || 0}
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-400">Total Quantity</div>
            <div className="text-2xl font-bold text-white">
              {order.order_items?.reduce((sum, item) => sum + item.quantity, 0).toFixed(2) || '0.00'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-400">Item Subtotal</div>
            <div className="text-2xl font-bold text-white">
              <CurrencyDisplay value={itemTotals.subtotal} size="lg" />
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-400">Modifiers Total</div>
            <div className="text-2xl font-bold text-white">
              <CurrencyDisplay value={itemTotals.modifierTotal} size="lg" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPayments = () => (
    <div className="space-y-4">
      {order.order_payments?.map((payment) => (
        <div key={payment.id} className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded ${
                payment.payment_type?.code === 'cash' ? 'bg-green-900/30' :
                payment.payment_type?.code === 'card' ? 'bg-blue-900/30' :
                payment.payment_type?.code === 'credit' ? 'bg-orange-900/30' :
                'bg-gray-700/30'
              }`}>
                {payment.payment_type?.code === 'cash' && <DollarSign size={20} className="text-green-400" />}
                {payment.payment_type?.code === 'card' && <CreditCard size={20} className="text-blue-400" />}
                {payment.payment_type?.code === 'credit' && <CreditCard size={20} className="text-orange-400" />}
              </div>
              <div>
                <div className="text-white font-medium">
                  {payment.payment_type?.name || 'Payment'}
                </div>
                <div className="text-sm text-gray-500">
                  {format(parseISO(payment.payment_date), 'MMM dd, yyyy HH:mm')}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-white">
                <CurrencyDisplay value={payment.amount} />
              </div>
              {payment.is_voided && (
                <StatusBadge status="voided" color="red" size="sm" />
              )}
            </div>
          </div>

          {/* Payment Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            {payment.tendered_amount && (
              <div>
                <div className="text-gray-400">Tendered</div>
                <div className="text-white"><CurrencyDisplay value={payment.tendered_amount} /></div>
              </div>
            )}
            {payment.change_amount > 0 && (
              <div>
                <div className="text-gray-400">Change</div>
                <div className="text-yellow-400"><CurrencyDisplay value={payment.change_amount} /></div>
              </div>
            )}
            {payment.reference_number && (
              <div>
                <div className="text-gray-400">Reference</div>
                <div className="text-white font-mono">{payment.reference_number}</div>
              </div>
            )}
            {payment.card_last_four && (
              <div>
                <div className="text-gray-400">Card</div>
                <div className="text-white">**** {payment.card_last_four}</div>
              </div>
            )}
            {payment.authorization_code && (
              <div>
                <div className="text-gray-400">Authorization</div>
                <div className="text-white font-mono">{payment.authorization_code}</div>
              </div>
            )}
            {payment.notes && (
              <div className="col-span-2">
                <div className="text-gray-400">Notes</div>
                <div className="text-white">{payment.notes}</div>
              </div>
            )}
          </div>
        </div>
      ))}

      {(!order.order_payments || order.order_payments.length === 0) && (
        <div className="text-center py-12 text-gray-500">
          <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
          <div className="text-lg">No payments recorded</div>
        </div>
      )}
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-800">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gray-800/90 backdrop-blur-sm border-b border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-400" />
                </button>
                <h2 className="text-xl font-bold text-white">Order Details</h2>
              </div>

              <div className="flex items-center gap-2">
                {/* Print Actions */}
                <div className="relative group">
                  <button
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Print"
                  >
                    <Printer size={20} className="text-gray-400" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-1">
                      <button
                        onClick={() => handlePrint('customer')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Printer size={14} />
                        Customer Receipt
                      </button>
                      <button
                        onClick={() => handlePrint('kitchen')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Printer size={14} />
                        Kitchen Ticket
                      </button>
                      {order.fiscal_receipt_number && (
                        <button
                          onClick={() => handlePrint('fiscal')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Printer size={14} />
                          Fiscal Copy
                        </button>
                      )}
                      <div className="border-t border-gray-700 my-1"></div>
                      <button
                        onClick={() => {/* Export PDF */}}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Download size={14} />
                        Export as PDF
                      </button>
                    </div>
                  </div>
                </div>

                {/* Send Actions */}
                {order.customer?.email && (
                  <div className="relative group">
                    <button
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Send Receipt"
                    >
                      <Send size={20} className="text-gray-400" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="py-1">
                        <button
                          onClick={() => handleSend('email')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Mail size={14} />
                          Email Receipt
                        </button>
                        {order.customer?.phone && (
                          <button
                            onClick={() => handleSend('sms')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                          >
                            <MessageSquare size={14} />
                            SMS Receipt
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* More Actions */}
                <div className="relative group">
                  <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                    <MoreVertical size={20} className="text-gray-400" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-1">
                      {order.status === 'open' && !order.is_voided && (
                        <>
                          <button
                            onClick={() => setShowVoidDialog(true)}
                            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <Ban size={14} />
                            Void Order
                          </button>
                          <button
                            onClick={() => setShowRefundDialog(true)}
                            className="w-full text-left px-4 py-2 text-sm text-orange-400 hover:bg-orange-900/20 flex items-center gap-2"
                          >
                            <RotateCcw size={14} />
                            Process Refund
                          </button>
                          <div className="border-t border-gray-700 my-1"></div>
                          <button
                            onClick={() => {/* Open split modal */}}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Share2 size={14} />
                            Split Order
                          </button>
                          <button
                            onClick={() => {/* Open transfer modal */}}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Truck size={14} />
                            Transfer Table
                          </button>
                        </>
                      )}
                      {order.status === 'voided' && (
                        <button
                          onClick={handleReopen}
                          disabled={isLoading}
                          className="w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-green-900/20 flex items-center gap-2 disabled:opacity-50"
                        >
                          <RotateCw size={14} />
                          Reopen Order
                        </button>
                      )}
                      <div className="border-t border-gray-700 my-1"></div>
                      <button
                        onClick={() => {/* Archive order */}}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Archive size={14} />
                        Archive Order
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mt-4">
              {[
                { id: 'details', label: 'Details', icon: FileText },
                { id: 'items', label: 'Items', icon: Package },
                { id: 'payments', label: 'Payments', icon: CreditCard },
                { id: 'history', label: 'History', icon: History }
              ].map(tab => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" text="Processing..." />
              </div>
            ) : (
              <>
                {activeTab === 'details' && renderOrderDetails()}
                {activeTab === 'items' && renderOrderItems()}
                {activeTab === 'payments' && renderPayments()}
                {activeTab === 'history' && (
                  <div className="text-center py-12 text-gray-500">
                    <History size={48} className="mx-auto mb-4 opacity-50" />
                    <div className="text-lg">Order history will appear here</div>
                    <div className="text-sm mt-2">Track changes, modifications, and status updates</div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-800/90 backdrop-blur-sm border-t border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Created by {order.user?.first_name} {order.user?.last_name}
                {order.updated_at !== order.created_at && (
                  <span className="ml-2">
                    • Last updated {format(parseISO(order.updated_at), 'MMM dd, HH:mm')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    // Navigate to POS for this order
                    window.open(`/app/pos?order=${order.id}`, '_blank')
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <ExternalLink size={16} />
                  Open in POS
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Void Order Dialog */}
      {showVoidDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowVoidDialog(false)} />
          <div className="relative bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-2">Void Order</h3>
            <p className="text-gray-400 mb-4">
              Are you sure you want to void order <span className="font-semibold text-white">#{order.order_number}</span>? 
              This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-2">Void Reason *</label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Enter reason for voiding this order..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowVoidDialog(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleVoid}
                disabled={!voidReason.trim() || isLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {isLoading ? 'Voiding...' : 'Void Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Dialog */}
      {showRefundDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowRefundDialog(false)} />
          <div className="relative bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-2">Process Refund</h3>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Refund Amount *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)}
                    min="0"
                    max={order.paid_amount}
                    step="0.01"
                    className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Max refundable: <CurrencyDisplay value={order.paid_amount} />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Refund Method</label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="original">Original Payment Method</option>
                  <option value="cash">Cash</option>
                  <option value="store_credit">Store Credit</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Reason for Refund</label>
                <textarea
                  placeholder="Enter reason for refund..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRefundDialog(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRefund}
                disabled={refundAmount <= 0 || refundAmount > order.paid_amount || isLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {isLoading ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}